import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    Alert,
    Button,
    Descriptions,
    Form,
    Input,
    Modal,
    Space,
    Spin,
    Steps,
    Tag,
    Typography,
    message
} from 'antd';
import { kedoGet, kedoPost } from './kedoApi';

const { Title, Paragraph, Text } = Typography;

const KedoDocumentPage = () => {
    const { id } = useParams();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [smsOpen, setSmsOpen] = useState(false);
    const [challenge, setChallenge] = useState(null);
    const [smsForm] = Form.useForm();
    const [routeForm] = Form.useForm();

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            setDoc(await kedoGet(`/documents/${id}`));
        } catch (e) {
            setError(
                e?.response?.data?.message || e.message || 'Документ не найден'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const startSms = async values => {
        try {
            const data = await kedoPost(`/documents/${id}/sign/sms/start`, {
                phone: values.phone
            });
            setChallenge(data);
            message.success(
                data?.stub_code
                    ? `Код (stub): ${data.stub_code}`
                    : 'Код отправлен'
            );
        } catch (e) {
            message.error(e?.response?.data?.message || e.message);
        }
    };

    const confirmSms = async values => {
        try {
            await kedoPost(`/documents/${id}/sign/sms/confirm`, {
                challenge_id: challenge?.challenge_id || challenge?.id,
                code: values.code
            });
            message.success('Документ подписан (ПЭП SMS)');
            setSmsOpen(false);
            setChallenge(null);
            smsForm.resetFields();
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || e.message);
        }
    };

    const signGoskey = async () => {
        try {
            const data = await kedoPost(`/documents/${id}/sign/goskey`, {});
            message.success(
                data?.goskey_document_id
                    ? `Отправлено в Госключ #${data.goskey_document_id}`
                    : 'Запрос Госключ создан'
            );
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || e.message);
        }
    };

    const setRoute = async values => {
        try {
            const steps = String(values.assignees || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .map((userId, idx) => ({
                    step_order: idx + 1,
                    step_type: idx === 0 ? 'approve' : 'sign',
                    assignee_user_id: Number(userId)
                }));
            await kedoPost(`/documents/${id}/route`, { steps });
            message.success('Маршрут сохранён');
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || e.message);
        }
    };

    const routeAction = async action => {
        try {
            await kedoPost(`/documents/${id}/route/action`, { action });
            message.success('Действие выполнено');
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || e.message);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 80 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                type="error"
                message={error}
                action={
                    <Link to="/kedo/app/documents">
                        <Button>К списку</Button>
                    </Link>
                }
            />
        );
    }

    const steps = doc?.route_steps || doc?.RouteSteps || [];
    const signatures = doc?.signatures || doc?.Signatures || [];
    const version = doc?.current_version || doc?.versions?.[0];

    return (
        <>
            <Space style={{ marginBottom: 8 }}>
                <Link to="/kedo/app/documents">← Документы</Link>
            </Space>
            <Title level={2} style={{ fontFamily: 'Georgia, serif', marginTop: 0 }}>
                {doc.title}
            </Title>
            <Paragraph type="secondary">{doc.description || 'Без описания'}</Paragraph>

            <div className="kedo-panel">
                <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
                    <Descriptions.Item label="ID">{doc.id}</Descriptions.Item>
                    <Descriptions.Item label="Статус">
                        <Tag>{doc.status}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Тип">{doc.doc_type}</Descriptions.Item>
                    <Descriptions.Item label="Направление">
                        {doc.direction || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="SHA-256">
                        <Text code copyable style={{ fontSize: 11 }}>
                            {version?.content_hash || '—'}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="external_refs">
                        <Text code style={{ fontSize: 11 }}>
                            {JSON.stringify(doc.external_refs || {})}
                        </Text>
                    </Descriptions.Item>
                </Descriptions>
                <Space wrap style={{ marginTop: 16 }}>
                    <Button
                        onClick={async () => {
                            try {
                                const token = localStorage.getItem('kedo_token');
                                const res = await fetch(
                                    `/api/kedo/v1/documents/${id}/download`,
                                    {
                                        headers: {
                                            Authorization: `Bearer ${token}`
                                        }
                                    }
                                );
                                if (!res.ok) throw new Error('Ошибка скачивания');
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download =
                                    version?.original_name || `kedo-${id}.bin`;
                                a.click();
                                URL.revokeObjectURL(url);
                            } catch (e) {
                                message.error(e.message || 'Скачивание не удалось');
                            }
                        }}
                    >
                        Скачать оригинал
                    </Button>
                    <Button type="primary" onClick={() => setSmsOpen(true)}>
                        Подписать SMS (ПЭП)
                    </Button>
                    <Button onClick={signGoskey}>Госключ</Button>
                    <Button onClick={() => routeAction('approve')}>
                        Согласовать
                    </Button>
                    <Button danger onClick={() => routeAction('reject')}>
                        Отклонить
                    </Button>
                </Space>
            </div>

            <div className="kedo-panel">
                <h2>Маршрут</h2>
                {steps.length ? (
                    <Steps
                        direction="vertical"
                        size="small"
                        items={steps.map(s => ({
                            title: `${s.step_type} · user ${s.assignee_user_id}`,
                            description: s.status || s.state,
                            status:
                                s.status === 'done' || s.state === 'done'
                                    ? 'finish'
                                    : s.status === 'active'
                                      ? 'process'
                                      : 'wait'
                        }))}
                    />
                ) : (
                    <Form
                        layout="vertical"
                        form={routeForm}
                        onFinish={setRoute}
                        style={{ maxWidth: 480 }}
                    >
                        <Form.Item
                            name="assignees"
                            label="ID пользователей через запятую (согласующий, подписант…)"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="1, 2" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit">
                            Назначить маршрут
                        </Button>
                    </Form>
                )}
            </div>

            <div className="kedo-panel">
                <h2>Подписи</h2>
                {signatures.length ? (
                    <ul>
                        {signatures.map(s => (
                            <li key={s.id}>
                                #{s.id} · {s.type || s.sign_type} ·{' '}
                                {s.provider} · {s.status}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <Text type="secondary">Пока нет подписей</Text>
                )}
            </div>

            <Modal
                title="Подписание SMS (ПЭП)"
                open={smsOpen}
                onCancel={() => {
                    setSmsOpen(false);
                    setChallenge(null);
                }}
                footer={null}
                destroyOnClose
            >
                {!challenge ? (
                    <Form layout="vertical" form={smsForm} onFinish={startSms}>
                        <Form.Item
                            name="phone"
                            label="Телефон"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="+79001234567" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            Отправить код
                        </Button>
                    </Form>
                ) : (
                    <Form layout="vertical" form={smsForm} onFinish={confirmSms}>
                        {challenge.stub_code ? (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 12 }}
                                message={`Dev stub: ${challenge.stub_code}`}
                            />
                        ) : null}
                        <Form.Item
                            name="code"
                            label="Код из SMS"
                            rules={[{ required: true }]}
                        >
                            <Input />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            Подтвердить подпись
                        </Button>
                    </Form>
                )}
            </Modal>
        </>
    );
};

export default KedoDocumentPage;
